import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { HotToastService } from '@ngxpert/hot-toast';
import { IssueService } from '../../../services/issue.service';
import { IssueCategory } from '../../../models/issue';
import { CurrentUserState } from '../../../state/current-user.state';

@Component({
  selector: 'app-issue-category-list',
  imports: [
    CommonModule, FormsModule,
    MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule
  ],
  template: `
    <div class="component-container">
      <div class="component-header">
        <h2>Issue categories</h2>
      </div>

      @if (canCreate) {
        <div class="component-box add-category">
          <mat-form-field appearance="fill">
            <mat-label>New category</mat-label>
            <input matInput [(ngModel)]="newCategoryName" (keyup.enter)="addCategory()" />
          </mat-form-field>
          <button mat-flat-button color="primary" [disabled]="!newCategoryName.trim()" (click)="addCategory()">
            <mat-icon>add</mat-icon> Add
          </button>
        </div>
      }

      @for (cat of categories; track cat.id) {
        <section class="section-container nrg-border-radius category">
          <div class="section-header">
            <span class="cat-name">{{ cat.name }}</span>
            @if (canDelete) {
              <button mat-icon-button color="warn" (click)="deleteCategory(cat)"><mat-icon>delete</mat-icon></button>
            }
          </div>
          <div class="section-content">
            <div class="subs">
              @for (sub of cat.subCategories; track sub.id) {
                <span class="sub-chip">
                  {{ sub.name }}
                  @if (canDelete) {
                    <mat-icon class="remove" (click)="deleteSub(cat, sub.id)">close</mat-icon>
                  }
                </span>
              }
              @if (cat.subCategories.length === 0) { <span class="muted">No sub-categories</span> }
            </div>

            @if (canCreate) {
              <div class="add-sub">
                <mat-form-field appearance="fill">
                  <mat-label>New sub-category</mat-label>
                  <input matInput [(ngModel)]="newSubName[cat.id]" (keyup.enter)="addSub(cat)" />
                </mat-form-field>
                <button mat-stroked-button [disabled]="!(newSubName[cat.id] || '').trim()" (click)="addSub(cat)">Add</button>
              </div>
            }
          </div>
        </section>
      }

      @if (categories.length === 0) { <p class="muted">No categories yet.</p> }
    </div>
  `,
  styles: [`
    /* Header stays full width; cards are capped so they don't stretch across the page */
    .component-box {
      max-width: 800px;
      margin-bottom: 1rem;
      padding: 1rem;
      background-color: color-mix(in srgb, var(--mat-sys-primary), transparent 95%) !important;
      border-radius: var(--mat-expansion-container-shape, 12px);
    }
    .add-category, .add-sub { display: flex; gap: 12px; align-items: center; }
    .section-container {
      max-width: 800px;
      background-color: color-mix(in srgb, var(--mat-sys-primary), transparent 95%) !important;
      padding: 1rem;
      margin-bottom: 1rem;
    }
    .section-container .section-header {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 1rem; color: var(--mat-sys-primary);
    }
    .cat-name { font-weight: 600; }
    .section-content { display: flex; flex-direction: column; }
    .subs { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
    .sub-chip {
      display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 12px;
      background: var(--mat-sys-secondary-container); color: var(--mat-sys-on-secondary-container);
      font-size: 0.85em; font-weight: 500;
    }
    .sub-chip .remove { font-size: 16px; height: 16px; width: 16px; cursor: pointer; opacity: 0.7; }
    .sub-chip .remove:hover { opacity: 1; }
    .muted { color: rgba(0,0,0,0.55); }
  `]
})
export class IssueCategoryListComponent implements OnInit {
  private issueService = inject(IssueService);
  private toast = inject(HotToastService);
  private currentUserState = inject(CurrentUserState);

  categories: IssueCategory[] = [];
  newCategoryName = '';
  newSubName: Record<string, string> = {};

  get canCreate(): boolean { return this.currentUserState.canCreate('Issues', 'Categories'); }
  get canDelete(): boolean { return this.currentUserState.canDelete('Issues', 'Categories'); }

  ngOnInit(): void { this.load(); }

  load(): void {
    this.issueService.getCategories().subscribe({
      next: c => (this.categories = c),
      error: () => this.toast.error('Error loading categories')
    });
  }

  addCategory(): void {
    const name = this.newCategoryName.trim();
    if (!name) return;
    this.issueService.createCategory({ name }).subscribe({
      next: () => { this.newCategoryName = ''; this.load(); },
      error: () => this.toast.error('Could not add category')
    });
  }

  deleteCategory(cat: IssueCategory): void {
    if (!confirm(`Delete category "${cat.name}"?`)) return;
    this.issueService.deleteCategory(cat.id).subscribe({
      next: () => { this.toast.success('Category deleted successfully'); this.load(); },
      error: () => this.toast.error('Could not delete (it may be in use)')
    });
  }

  addSub(cat: IssueCategory): void {
    const name = (this.newSubName[cat.id] || '').trim();
    if (!name) return;
    this.issueService.createSubCategory({ name, categoryId: cat.id }).subscribe({
      next: () => { this.newSubName[cat.id] = ''; this.load(); },
      error: () => this.toast.error('Could not add sub-category')
    });
  }

  deleteSub(cat: IssueCategory, subId: string): void {
    this.issueService.deleteSubCategory(subId).subscribe({
      next: () => { this.toast.success('Sub-category deleted successfully'); this.load(); },
      error: () => this.toast.error('Could not delete (it may be in use)')
    });
  }
}
